import type { ReactNode } from 'react';
import {
  IconBolt,
  IconCalendar,
  IconCalendarEvent,
  IconChecklist,
  IconChartBar,
  IconChartDots,
  IconCertificate,
  IconDeviceGamepad2,
  IconFileAnalytics,
  IconFileDescription,
  IconFileText,
  IconFolder,
  IconHelp,
  IconHome,
  IconId,
  IconLock,
  IconMovie,
  IconRocket,
  IconSitemap,
  IconTarget,
  IconTicket,
  IconTool,
  IconTrophy,
  IconUserCog,
  IconUserSearch,
  IconUsers,
} from '@tabler/icons-react';

interface IconSpec {
  icon?: (props: { size?: number; style?: Record<string, string> }) => ReactNode;
  color?: string;
  imageSrc?: string;
}

const ICON_MAP: Record<string, IconSpec> = {
  Home: { icon: IconHome, color: '#FFD700' },
  'Insight Center': { icon: IconBolt, color: '#1E90FF' },
  'Onboarding Game': { icon: IconDeviceGamepad2, color: '#FF4500' },
  Licensing: { icon: IconId, color: '#1E90FF' },
  'Track My License': { icon: IconChecklist, color: '#1E90FF' },
  'Licensing Documents': { icon: IconFileDescription, color: '#C0C0C0' },
  'Crash Course': { icon: IconRocket, color: '#FFA500' },
  '10 Systematic Tools': { icon: IconTool, color: '#1E90FF' },
  'My Team': { icon: IconUsers, color: '#F4A7B9' },
  'Org Chart': { icon: IconSitemap, color: '#F4A7B9' },
  'Prospect Tracker': { icon: IconUserSearch, color: '#1E90FF' },
  'Mission Tracker': { icon: IconTarget, color: '#32CD32' },
  'Associate Tracker': { icon: IconUsers, color: '#F4A7B9' },
  Builders: { icon: IconChartDots, color: '#FFD700' },
  'Licensing Tracker': { icon: IconCertificate, color: '#F5F5F5' },
  'Production Tracker': {
    imageSrc:
      'https://firebasestorage.googleapis.com/v0/b/wealthbuilders-crm-9c323.firebasestorage.app/o/ChatGPT%20Image%20Sep%2014%2C%202025%2C%2010_07_20%20PM.png?alt=media&token=8bcac8b2-de4a-4e19-83c6-f3beb8d82362',
  },
  Matchup: { icon: IconUsers, color: '#32CD32' },
  BPM: { icon: IconChartBar, color: '#1E90FF' },
  'BPM Overview': { icon: IconChartDots, color: '#1E90FF' },
  'Add Guest': { icon: IconUserSearch, color: '#1E90FF' },
  'Guest Invites': { icon: IconFileAnalytics, color: '#FFA500' },
  'Associate Invites': { icon: IconFileAnalytics, color: '#22D3EE' },
  'Associate Check-In': { icon: IconChecklist, color: '#32CD32' },
  'Guest Check-In': { icon: IconChecklist, color: '#32CD32' },
  'BPM Schedule': { icon: IconCalendarEvent, color: '#1E90FF' },
  'Training Center': { icon: IconTarget, color: '#32CD32' },
  Training: { icon: IconTarget, color: '#32CD32' },
  Promotions: { icon: IconTrophy, color: '#FFD700' },
  'Training Schedule': { icon: IconCalendarEvent, color: '#32CD32' },
  Calendar: { icon: IconCalendar, color: '#1E90FF' },
  'File Vault': { icon: IconFolder, color: '#C0C0C0' },
  'Help Desk': { icon: IconHelp, color: '#F4A7B9' },
  'Big Event': { icon: IconTicket, color: '#FFA500' },
  'Big Event Builder': { icon: IconTicket, color: '#FFA500' },
  Purchases: { icon: IconFileText, color: '#FFD700' },
  'Check-in': { icon: IconChecklist, color: '#32CD32' },
  Permissions: { icon: IconLock, color: '#1E90FF' },
  'Recognition Orders': { icon: IconTrophy, color: '#FFD700' },
  Reports: { icon: IconChartBar, color: '#1E90FF' },
  Admin: { icon: IconUserCog, color: '#FFD700' },
  'Home Content': { icon: IconMovie, color: '#FFD700' },
};

export function getSidebarMenuIcon(
  label: string,
  fallbackIcon?: ReactNode,
  size = 20
): ReactNode {
  const spec = ICON_MAP[label];

  if (spec?.imageSrc) {
    return (
      <img
        src={spec.imageSrc}
        alt={label}
        style={{ width: size, height: size, objectFit: 'contain' }}
      />
    );
  }

  if (spec?.icon) {
    const IconComponent = spec.icon;
    return <IconComponent size={size} style={{ color: spec.color ?? '#ffffff' }} />;
  }

  if (fallbackIcon) {
    return fallbackIcon;
  }

  return <IconFileAnalytics size={size} style={{ color: '#ffffff' }} />;
}
